import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, FileText, Eye, Loader2, Calendar, Upload, X, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rich Text Editor Component
const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      TextStyle,
      Color,
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: 'Start writing your story here...',
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          editor.chain().focus().setImage({ src: reader.result }).run();
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const setLink = () => {
    let url = window.prompt('Enter URL:');
    if (url) {
      // Ensure URL has protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      editor.chain().focus().setLink({ href: url, target: '_blank' }).run();
    }
  };

  if (!editor) return null;

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-gray-300 p-2 flex flex-wrap gap-1 bg-gray-50">
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className="h-8 px-2"
        >
          H1
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className="h-8 px-2"
        >
          H2
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className="h-8 px-2"
        >
          H3
        </Button>
        
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('bold') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="h-8 px-2"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('italic') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="h-8 px-2"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('underline') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className="h-8 px-2"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('bulletList') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className="h-8 px-2"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive('orderedList') ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className="h-8 px-2"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className="h-8 px-2"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className="h-8 px-2"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'outline'}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className="h-8 px-2"
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        
        <div className="w-px h-8 bg-gray-300 mx-1" />
        
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={setLink}
          className="h-8 px-2"
        >
          <Link2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addImage}
          className="h-8 px-2"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
      </div>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-lg max-w-none p-4 min-h-[400px] focus:outline-none"
      />
    </div>
  );
};

export default function AdminStories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Startup options for linking
  const [startups, setStartups] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    startup_id: '',
    title: '',
    short_description: '',
    submission_type: 'Self Written',
    article_content: '',
    banner_photo: null,
    founder_profiles: []
  });

  useEffect(() => {
    fetchStories();
    fetchStartups();
  }, []);

  const fetchStories = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/stories/?status=Published`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStories(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const fetchStartups = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/startups/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStartups(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load startups');
    }
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [field]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFounderPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          founder_profiles: [...prev.founder_profiles, {
            founder_name: '',
            founder_designation: '',
            founder_email: 'admin@starticals.com',
            profile_picture: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const updateFounderInfo = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      founder_profiles: prev.founder_profiles.map((profile, i) => 
        i === index ? { ...profile, [field]: value } : profile
      )
    }));
  };

  const removeFounderPhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      founder_profiles: prev.founder_profiles.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.startup_id) {
      toast.error('Please select a startup');
      return;
    }
    
    if (!formData.title || !formData.short_description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    if (!formData.article_content) {
      toast.error('Please write the story content');
      return;
    }

    setSaving(true);
    
    try {
      const token = Cookies.get('admin_token');
      
      // Find selected startup
      const selectedStartup = startups.find(s => s.id === formData.startup_id);
      
      const storyData = {
        startup_id: formData.startup_id,
        startup_name: selectedStartup?.startup_name || 'Unknown',
        story_token: `admin-${Date.now()}`,
        founder_email: 'admin@starticals.com',
        title: formData.title,
        short_description: formData.short_description,
        submission_type: formData.submission_type,
        article_content: formData.article_content,
        banner_photo: formData.banner_photo,
        founder_profiles: formData.founder_profiles,
        status: 'Submitted',
        is_draft: false,
        submitted_at: new Date().toISOString()
      };

      await axios.post(`${API}/stories/admin/create`, storyData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Story saved to Pending Submissions!');
      setIsModalOpen(false);
      fetchStories();
      
      // Reset form
      setFormData({
        startup_id: '',
        title: '',
        short_description: '',
        submission_type: 'Self Written',
        article_content: '',
        banner_photo: null,
        founder_profiles: []
      });
    } catch (err) {
      console.error('Error creating story:', err);
      toast.error(err.response?.data?.detail || 'Failed to create story');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Stories</h1>
          <p className="text-muted-foreground">Manage published startup stories and articles</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Story
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Story</DialogTitle>
              <DialogDescription>
                Create a new startup story and save it to Pending Submissions for review
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Select Startup */}
              <div className="space-y-2">
                <Label htmlFor="startup">Select Startup *</Label>
                <Select 
                  id="startup"
                  value={formData.startup_id} 
                  onChange={(e) => setFormData(prev => ({ ...prev, startup_id: e.target.value }))}
                  required
                >
                  <option value="">Choose a startup</option>
                  {startups.map((startup) => (
                    <option key={startup.id} value={startup.id}>
                      {startup.startup_name}
                    </option>
                  ))}
                </Select>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Story Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter story title"
                  required
                />
              </div>

              {/* Short Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Short Description *</Label>
                <Textarea
                  id="description"
                  value={formData.short_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                  placeholder="Brief description for preview"
                  rows={3}
                  required
                />
              </div>

              {/* Submission Type */}
              <div className="space-y-2">
                <Label htmlFor="type">Story Type</Label>
                <Select 
                  id="type"
                  value={formData.submission_type} 
                  onChange={(e) => setFormData(prev => ({ ...prev, submission_type: e.target.value }))}
                >
                  <option value="Self Written">Self Written</option>
                  <option value="Expert Written">Expert Written</option>
                  <option value="Questionnaire">Questionnaire</option>
                </Select>
              </div>

              {/* Banner Photo Upload */}
              <div className="space-y-2">
                <Label htmlFor="banner">Banner Photo</Label>
                <div className="space-y-3">
                  <Input
                    id="banner"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'banner_photo')}
                  />
                  {formData.banner_photo && (
                    <div className="relative w-full h-48 border rounded-lg overflow-hidden">
                      <img 
                        src={formData.banner_photo} 
                        alt="Banner preview" 
                        className="w-full h-full object-cover"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setFormData(prev => ({ ...prev, banner_photo: null }))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Founder Profile Photos */}
              <div className="space-y-2">
                <Label>Founder Profiles</Label>
                <div className="space-y-4">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleFounderPhotoUpload}
                  />
                  <p className="text-sm text-muted-foreground">Upload each founder's photo and add their details</p>
                  
                  {formData.founder_profiles.length > 0 && (
                    <div className="space-y-4">
                      {formData.founder_profiles.map((profile, index) => (
                        <div key={index} className="border rounded-lg p-4 relative">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2 h-8 w-8 p-0 rounded-full"
                            onClick={() => removeFounderPhoto(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          
                          <div className="flex gap-4">
                            <img 
                              src={profile.profile_picture} 
                              alt={`Founder ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-lg border-2 border-primary flex-shrink-0"
                            />
                            <div className="flex-1 space-y-3">
                              <div>
                                <Label htmlFor={`founder-name-${index}`}>Founder Name *</Label>
                                <Input
                                  id={`founder-name-${index}`}
                                  value={profile.founder_name || ''}
                                  onChange={(e) => updateFounderInfo(index, 'founder_name', e.target.value)}
                                  placeholder="John Doe"
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor={`founder-designation-${index}`}>Designation</Label>
                                <Input
                                  id={`founder-designation-${index}`}
                                  value={profile.founder_designation || ''}
                                  onChange={(e) => updateFounderInfo(index, 'founder_designation', e.target.value)}
                                  placeholder="CEO, CTO, etc. (optional)"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Rich Text Editor */}
              <div className="space-y-2">
                <Label>Story Content *</Label>
                <RichTextEditor 
                  content={formData.article_content}
                  onChange={(content) => setFormData(prev => ({ ...prev, article_content: content }))}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Save Story
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Published Stories ({stories.length})</CardTitle>
          <CardDescription>View and manage all published stories</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : stories.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="mb-2">No stories published yet</p>
              <p className="text-sm">Published stories will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stories.map((story) => (
                <div key={story.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold">{story.title || story.startup_name}</h3>
                        <Badge className="bg-green-500/10 text-green-700 border-green-500/20">
                          Published
                        </Badge>
                        <Badge variant="outline">
                          {story.submission_type || 'Questionnaire'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {story.short_description || story.brief_info?.substring(0, 150) + '...'}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(story.submitted_at || story.created_at)}
                        </span>
                        <span>By: {story.startup_name}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/story/${story.slug}`, '_blank')}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    </div>
                  </div>
                  {story.banner_photo && (
                    <div className="mt-3">
                      <img 
                        src={story.banner_photo} 
                        alt="Story banner" 
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
